// api/progression.ts
import { fetchAPI } from "./utils";

export type ImageRecognitionProvider = "OPENAI" | "HORAMA" | "WEAVIATE";

// Scan Config
export interface CreateScanConfigParams {
    token: string;
    trackId: string;
    themeName: string;
    referenceImageUrl?: string;
    language: string;
    imageRecognitionProvider?: ImageRecognitionProvider;
}

export interface GetScanConfigParams {
    token: string;
    trackId: string;
}

export interface UpdateScanConfigParams {
    token: string;
    scanConfigId: string;
    themeName?: string;
    referenceImageUrl?: string;
    language?: string;
    imageRecognitionProvider?: ImageRecognitionProvider;
    confirmProviderChange?: boolean;
    /**
     * Re-run the Weaviate indexing on a config already on WEAVIATE. Needed because the
     * indexing otherwise only runs on a provider switch, which leaves a track stranded
     * with zero vectors if that one run fails.
     */
    resyncWeaviate?: boolean;
}

export interface ProviderChangeImpact {
    requiresConfirmation: boolean;
    message: string;
    currentProvider: string;
    newProvider: string;
    totalObjects: number;
    affectedObjects: Array<{
        id: string;
        name: string;
        currentKey: string;
        issue: string;
        newKey?: string;
    }>;
    horamaSpecies?: string[];
    weaviateAction?: "create_collection" | "delete_collection" | null;
    objectsWithImage?: number;
    objectsWithoutImage?: number;
    severity: "high" | "low";
}

export interface DeleteScanConfigParams {
    token: string;
    scanConfigId: string;
}

// Scannable Objects
export interface CreateScannableObjectParams {
    token: string;
    scanConfigId: string;
    name: string;
    referenceImageUrl?: string;
    position?: number;
    language: string;
    tagIds?: string[];
    recognitionKey?: string;
}

export interface GetScannableObjectsParams {
    token: string;
    scanConfigId: string;
}

export interface UpdateScannableObjectParams {
    token: string;
    scannableObjectId: string;
    name?: string;
    referenceImageUrl?: string;
    position?: number;
    language?: string;
    tagIds?: string[];
    recognitionKey?: string;
}

export interface DeleteScannableObjectParams {
    token: string;
    scannableObjectId: string;
}

export interface UpdateScannableObjectStatusParams {
    token: string;
    scannableObjectId: string;
    status: boolean;
}

// Scannable Object Tags
export interface CreateScannableObjectTagParams {
    token: string;
    scanConfigId: string;
    name: string;
    language: string;
}

export interface GetScannableObjectTagsParams {
    token: string;
    scanConfigId: string;
}

export interface UpdateScannableObjectTagParams {
    token: string;
    scannableObjectTagId: string;
    name?: string;
    language?: string;
}

export interface DeleteScannableObjectTagParams {
    token: string;
    scannableObjectTagId: string;
}

export interface UpdateScannableObjectTagStatusParams {
    token: string;
    scannableObjectTagId: string;
    status: boolean;
}

// Progression Config
export interface CreateProgressionConfigParams {
    token: string;
    trackId: string;
    defaultProgressionImage?: string;
    defaultAvatarImage?: string;
}

export interface GetProgressionConfigParams {
    token: string;
    trackId: string;
}

export interface UpdateProgressionConfigParams {
    token: string;
    progressionConfigId: string;
    defaultProgressionImage?: string;
    defaultAvatarImage?: string;
}

// Levels
export interface CreateLevelParams {
    token: string;
    progressionConfigId: string;
    position: number;
    name: string;
    text?: string;
    discoveredWaypointsThreshold: number;
    imageUrl?: string;
    hasAvatarEvolution?: boolean;
    progressionImageOverride?: string;
    avatarImageOverride?: string;
    language: string;
}

export interface GetLevelsParams {
    token: string;
    progressionConfigId: string;
}

export interface UpdateLevelParams {
    token: string;
    levelId: string;
    position?: number;
    name?: string;
    text?: string;
    discoveredWaypointsThreshold?: number;
    imageUrl?: string;
    hasAvatarEvolution?: boolean;
    progressionImageOverride?: string;
    avatarImageOverride?: string;
    language?: string;
}

export interface DeleteLevelParams {
    token: string;
    levelId: string;
}

export interface UpdateLevelStatusParams {
    token: string;
    levelId: string;
    status: boolean;
}

// Badges
export interface BadgeCondition {
    conditionType: "TAG_COUNT" | "OBJECT_WAYPOINTS";
    tagId?: string;
    taggedObjectsRequired?: number;
    waypointsPerObjectRequired?: number;
}

export interface CreateBadgeParams {
    token: string;
    progressionConfigId: string;
    name: string;
    text?: string;
    imageUrl?: string;
    conditions: BadgeCondition[];
    language: string;
}

export interface GetBadgesParams {
    token: string;
    progressionConfigId: string;
}

export interface UpdateBadgeParams {
    token: string;
    badgeId: string;
    name?: string;
    text?: string;
    imageUrl?: string;
    conditions?: BadgeCondition[];
    language?: string;
}

export interface DeleteBadgeParams {
    token: string;
    badgeId: string;
}

export interface UpdateBadgeStatusParams {
    token: string;
    badgeId: string;
    status: boolean;
}

// Rewards
export interface CreateRewardParams {
    token: string;
    progressionConfigId: string;
    name: string;
    popupText?: string;
    descriptionText?: string;
    imageUrl?: string;
    levelId?: string;
    expirationDays?: number;
    language: string;
}

export interface GetRewardsParams {
    token: string;
    progressionConfigId: string;
}

export interface UpdateRewardParams {
    token: string;
    rewardId: string;
    name?: string;
    popupText?: string;
    descriptionText?: string;
    imageUrl?: string;
    levelId?: string;
    expirationDays?: number;
    language?: string;
}

export interface DeleteRewardParams {
    token: string;
    rewardId: string;
}

export interface UpdateRewardStatusParams {
    token: string;
    rewardId: string;
    status: boolean;
}

// API Response types
export interface ScannableObjectMutationResponse {
    scannableObject: ScannableObjectResponse;
    /** Set when the object was saved in Postgres but not indexed in Weaviate. */
    weaviateWarning?: string | null;
}

/**
 * Outcome of the Weaviate indexing that runs alongside a provider switch.
 * The endpoint still returns 200 when indexing fails, so this is the only signal
 * that the track's vectors are missing or incomplete.
 */
export interface WeaviateSyncResult {
    indexed: number;
    skipped: number;
    errors: string[];
    weaviateWarning?: string;
    /** Indexing succeeded but the track's previous vectors survived — possible duplicates. */
    staleVectorsWarning?: string;
    /** Gemini refused to embed some images: the run is incomplete but nothing is broken. */
    quotaExhausted?: boolean;
}

export interface ScanConfigResponse {
    scanConfig: {
        id: string;
        trackId: string;
        referenceImageUrl?: string;
        imageRecognitionProvider?: ImageRecognitionProvider;
        scanConfigLocalized: Array<{
            id: string;
            language: string;
            themeName: string;
        }>;
        scannableObjects: ScannableObjectResponse[];
        scannableObjectTags: ScannableObjectTagResponse[];
    };
    weaviateSync?: WeaviateSyncResult | null;
    weaviateWarning?: string | null;
    providerTransition?: string;
    /**
     * Index completeness, present only for WEAVIATE configs. `indexed` is null when the
     * count could not be obtained — the badge then reads "unknown" rather than "complete".
     */
    weaviateIndex?: WeaviateIndexStatus | null;
}

export interface WeaviateIndexStatus {
    /** Active objects that have a reference image, so that can be indexed at all. */
    expected: number;
    indexed: number | null;
    error?: string;
}

export interface ScannableObjectResponse {
    id: string;
    position: number;
    status: boolean;
    referenceImageUrl?: string;
    recognitionKey?: string;
    scannableObjectLocalized: Array<{
        id: string;
        language: string;
        name: string;
    }>;
    tags: ScannableObjectTagResponse[];
    waypoints?: Array<{
        id: string;
        position: number;
        scannableObjectId?: string;
        waypointLocalized?: Array<{
            name: string;
            language: string;
        }>;
    }>;
}

export interface ScannableObjectTagResponse {
    id: string;
    status: boolean;
    objectCount?: number;
    scannableObjectTagLocalized: Array<{
        id: string;
        language: string;
        name: string;
    }>;
}

export interface ProgressionConfigResponse {
    progressionConfig: {
        id: string;
        trackId: string;
        defaultProgressionImage?: string;
        defaultAvatarImage?: string;
        levels: LevelResponse[];
        badges: BadgeResponse[];
        rewards: RewardResponse[];
    };
}

export interface LevelResponse {
    id: string;
    position: number;
    status: boolean;
    discoveredWaypointsThreshold: number;
    imageUrl?: string;
    hasAvatarEvolution: boolean;
    progressionImageOverride?: string;
    avatarImageOverride?: string;
    levelLocalized: Array<{
        id: string;
        language: string;
        name: string;
        text?: string;
    }>;
}

export interface BadgeResponse {
    id: string;
    status: boolean;
    imageUrl?: string;
    badgeLocalized: Array<{
        id: string;
        language: string;
        name: string;
        text?: string;
    }>;
    conditions: Array<{
        id: string;
        conditionType: string;
        tagId?: string;
        taggedObjectsRequired?: number;
        waypointsPerObjectRequired?: number;
    }>;
}

export interface RewardResponse {
    id: string;
    status: boolean;
    imageUrl?: string;
    expirationDays?: number;
    levelId?: string;
    rewardLocalized: Array<{
        id: string;
        language: string;
        name: string;
        popupText?: string;
        descriptionText?: string;
    }>;
    level?: LevelResponse;
}

// Scan Config API functions
export const createScanConfig = async ({
    token,
    trackId,
    themeName,
    referenceImageUrl,
    language,
    imageRecognitionProvider,
}: CreateScanConfigParams): Promise<ScanConfigResponse> => {
    return fetchAPI<ScanConfigResponse>(
        "/create-scan-config",
        "POST",
        { trackId, themeName, referenceImageUrl, language, imageRecognitionProvider },
        token,
    );
};

export const getScanConfig = async ({ token, trackId }: GetScanConfigParams): Promise<ScanConfigResponse> => {
    return fetchAPI<ScanConfigResponse>(`/get-scan-config?trackId=${trackId}`, "GET", undefined, token);
};

export const updateScanConfig = async ({
    token,
    scanConfigId,
    themeName,
    referenceImageUrl,
    language,
    imageRecognitionProvider,
    confirmProviderChange,
    resyncWeaviate,
}: UpdateScanConfigParams): Promise<ScanConfigResponse | ProviderChangeImpact> => {
    return fetchAPI<ScanConfigResponse | ProviderChangeImpact>(
        "/update-scan-config",
        "POST",
        {
            scanConfigId,
            themeName,
            referenceImageUrl,
            language,
            imageRecognitionProvider,
            confirmProviderChange,
            resyncWeaviate,
        },
        token,
    );
};

export const deleteScanConfig = async ({ token, scanConfigId }: DeleteScanConfigParams): Promise<void> => {
    return fetchAPI<void>("/delete-scan-config", "POST", { scanConfigId }, token);
};

// Scannable Objects API functions
export const createScannableObject = async ({
    token,
    scanConfigId,
    name,
    referenceImageUrl,
    position,
    language,
    tagIds,
    recognitionKey,
}: CreateScannableObjectParams): Promise<ScannableObjectMutationResponse> => {
    return fetchAPI<ScannableObjectMutationResponse>(
        "/create-scannable-object",
        "POST",
        { scanConfigId, name, referenceImageUrl, position, language, tagIds, recognitionKey },
        token,
    );
};

export const getScannableObjects = async ({
    token,
    scanConfigId,
}: GetScannableObjectsParams): Promise<{ scannableObjects: ScannableObjectResponse[] }> => {
    return fetchAPI<{ scannableObjects: ScannableObjectResponse[] }>(
        `/get-scannable-objects?scanConfigId=${scanConfigId}`,
        "GET",
        undefined,
        token,
    );
};

export const updateScannableObject = async ({
    token,
    scannableObjectId,
    name,
    referenceImageUrl,
    position,
    language,
    tagIds,
    recognitionKey,
}: UpdateScannableObjectParams): Promise<ScannableObjectMutationResponse> => {
    return fetchAPI<ScannableObjectMutationResponse>(
        "/update-scannable-object",
        "POST",
        { scannableObjectId, name, referenceImageUrl, position, language, tagIds, recognitionKey },
        token,
    );
};

export const deleteScannableObject = async ({
    token,
    scannableObjectId,
}: DeleteScannableObjectParams): Promise<void> => {
    return fetchAPI<void>("/delete-scannable-object", "POST", { scannableObjectId }, token);
};

export interface UpdateScannableObjectPositionsParams {
    token: string;
    positions: Array<{ scannableObjectId: string; position: number }>;
}

export const updateScannableObjectPositions = async ({
    token,
    positions,
}: UpdateScannableObjectPositionsParams): Promise<{ message: string; count: number }> => {
    return fetchAPI<{ message: string; count: number }>(
        "/update-scannable-object-positions",
        "POST",
        { positions },
        token,
    );
};

export const updateScannableObjectStatus = async ({
    token,
    scannableObjectId,
    status,
}: UpdateScannableObjectStatusParams): Promise<{ scannableObject: ScannableObjectResponse }> => {
    return fetchAPI<{ scannableObject: ScannableObjectResponse }>(
        "/update-scannable-object-status",
        "POST",
        { scannableObjectId, status },
        token,
    );
};

// Scannable Object Tags API functions
export const createScannableObjectTag = async ({
    token,
    scanConfigId,
    name,
    language,
}: CreateScannableObjectTagParams): Promise<{ scannableObjectTag: ScannableObjectTagResponse }> => {
    return fetchAPI<{ scannableObjectTag: ScannableObjectTagResponse }>(
        "/create-scannable-object-tag",
        "POST",
        { scanConfigId, name, language },
        token,
    );
};

export const getScannableObjectTags = async ({
    token,
    scanConfigId,
}: GetScannableObjectTagsParams): Promise<{ scannableObjectTags: ScannableObjectTagResponse[] }> => {
    return fetchAPI<{ scannableObjectTags: ScannableObjectTagResponse[] }>(
        `/get-scannable-object-tags?scanConfigId=${scanConfigId}`,
        "GET",
        undefined,
        token,
    );
};

export const updateScannableObjectTag = async ({
    token,
    scannableObjectTagId,
    name,
    language,
}: UpdateScannableObjectTagParams): Promise<{ scannableObjectTag: ScannableObjectTagResponse }> => {
    return fetchAPI<{ scannableObjectTag: ScannableObjectTagResponse }>(
        "/update-scannable-object-tag",
        "POST",
        { scannableObjectTagId, name, language },
        token,
    );
};

export const deleteScannableObjectTag = async ({
    token,
    scannableObjectTagId,
}: DeleteScannableObjectTagParams): Promise<void> => {
    return fetchAPI<void>("/delete-scannable-object-tag", "POST", { scannableObjectTagId }, token);
};

export const updateScannableObjectTagStatus = async ({
    token,
    scannableObjectTagId,
    status,
}: UpdateScannableObjectTagStatusParams): Promise<{ scannableObjectTag: ScannableObjectTagResponse }> => {
    return fetchAPI<{ scannableObjectTag: ScannableObjectTagResponse }>(
        "/update-scannable-object-tag-status",
        "POST",
        { scannableObjectTagId, status },
        token,
    );
};

// Progression Config API functions
export const createProgressionConfig = async ({
    token,
    trackId,
    defaultProgressionImage,
    defaultAvatarImage,
}: CreateProgressionConfigParams): Promise<ProgressionConfigResponse> => {
    return fetchAPI<ProgressionConfigResponse>(
        "/create-progression-config",
        "POST",
        { trackId, defaultProgressionImage, defaultAvatarImage },
        token,
    );
};

export const getProgressionConfig = async ({
    token,
    trackId,
}: GetProgressionConfigParams): Promise<ProgressionConfigResponse> => {
    return fetchAPI<ProgressionConfigResponse>(`/get-progression-config?trackId=${trackId}`, "GET", undefined, token);
};

export const updateProgressionConfig = async ({
    token,
    progressionConfigId,
    defaultProgressionImage,
    defaultAvatarImage,
}: UpdateProgressionConfigParams): Promise<ProgressionConfigResponse> => {
    return fetchAPI<ProgressionConfigResponse>(
        "/update-progression-config",
        "POST",
        { progressionConfigId, defaultProgressionImage, defaultAvatarImage },
        token,
    );
};

// Levels API functions
export const createLevel = async ({
    token,
    progressionConfigId,
    position,
    name,
    text,
    discoveredWaypointsThreshold,
    imageUrl,
    hasAvatarEvolution,
    progressionImageOverride,
    avatarImageOverride,
    language,
}: CreateLevelParams): Promise<{ level: LevelResponse }> => {
    return fetchAPI<{ level: LevelResponse }>(
        "/create-progression-level",
        "POST",
        {
            progressionConfigId,
            position,
            name,
            text,
            discoveredWaypointsThreshold,
            imageUrl,
            hasAvatarEvolution,
            progressionImageOverride,
            avatarImageOverride,
            language,
        },
        token,
    );
};

export const getLevels = async ({
    token,
    progressionConfigId,
}: GetLevelsParams): Promise<{ levels: LevelResponse[] }> => {
    return fetchAPI<{ levels: LevelResponse[] }>(
        `/get-progression-levels?progressionConfigId=${progressionConfigId}`,
        "GET",
        undefined,
        token,
    );
};

export const updateLevel = async ({
    token,
    levelId,
    position,
    name,
    text,
    discoveredWaypointsThreshold,
    imageUrl,
    hasAvatarEvolution,
    progressionImageOverride,
    avatarImageOverride,
    language,
}: UpdateLevelParams): Promise<{ level: LevelResponse }> => {
    return fetchAPI<{ level: LevelResponse }>(
        "/update-progression-level",
        "POST",
        {
            levelId,
            position,
            name,
            text,
            discoveredWaypointsThreshold,
            imageUrl,
            hasAvatarEvolution,
            progressionImageOverride,
            avatarImageOverride,
            language,
        },
        token,
    );
};

export const deleteLevel = async ({ token, levelId }: DeleteLevelParams): Promise<void> => {
    return fetchAPI<void>("/delete-progression-level", "POST", { levelId }, token);
};

export const updateLevelStatus = async ({
    token,
    levelId,
    status,
}: UpdateLevelStatusParams): Promise<{ level: LevelResponse }> => {
    return fetchAPI<{ level: LevelResponse }>("/update-progression-level-status", "POST", { levelId, status }, token);
};

// Badges API functions
export const createBadge = async ({
    token,
    progressionConfigId,
    name,
    text,
    imageUrl,
    conditions,
    language,
}: CreateBadgeParams): Promise<{ badge: BadgeResponse }> => {
    return fetchAPI<{ badge: BadgeResponse }>(
        "/create-badge",
        "POST",
        { progressionConfigId, name, text, imageUrl, conditions, language },
        token,
    );
};

export const getBadges = async ({
    token,
    progressionConfigId,
}: GetBadgesParams): Promise<{ badges: BadgeResponse[] }> => {
    return fetchAPI<{ badges: BadgeResponse[] }>(
        `/get-badges?progressionConfigId=${progressionConfigId}`,
        "GET",
        undefined,
        token,
    );
};

export const updateBadge = async ({
    token,
    badgeId,
    name,
    text,
    imageUrl,
    conditions,
    language,
}: UpdateBadgeParams): Promise<{ badge: BadgeResponse }> => {
    return fetchAPI<{ badge: BadgeResponse }>(
        "/update-badge",
        "POST",
        { badgeId, name, text, imageUrl, conditions, language },
        token,
    );
};

export const deleteBadge = async ({ token, badgeId }: DeleteBadgeParams): Promise<void> => {
    return fetchAPI<void>("/delete-badge", "POST", { badgeId }, token);
};

export const updateBadgeStatus = async ({
    token,
    badgeId,
    status,
}: UpdateBadgeStatusParams): Promise<{ badge: BadgeResponse }> => {
    return fetchAPI<{ badge: BadgeResponse }>("/update-badge-status", "POST", { badgeId, status }, token);
};

// Rewards API functions
export const createReward = async ({
    token,
    progressionConfigId,
    name,
    popupText,
    descriptionText,
    imageUrl,
    levelId,
    expirationDays,
    language,
}: CreateRewardParams): Promise<{ reward: RewardResponse }> => {
    return fetchAPI<{ reward: RewardResponse }>(
        "/create-reward",
        "POST",
        { progressionConfigId, name, popupText, descriptionText, imageUrl, levelId, expirationDays, language },
        token,
    );
};

export const getRewards = async ({
    token,
    progressionConfigId,
}: GetRewardsParams): Promise<{ rewards: RewardResponse[] }> => {
    return fetchAPI<{ rewards: RewardResponse[] }>(
        `/get-rewards?progressionConfigId=${progressionConfigId}`,
        "GET",
        undefined,
        token,
    );
};

export const updateReward = async ({
    token,
    rewardId,
    name,
    popupText,
    descriptionText,
    imageUrl,
    levelId,
    expirationDays,
    language,
}: UpdateRewardParams): Promise<{ reward: RewardResponse }> => {
    return fetchAPI<{ reward: RewardResponse }>(
        "/update-reward",
        "POST",
        { rewardId, name, popupText, descriptionText, imageUrl, levelId, expirationDays, language },
        token,
    );
};

export const deleteReward = async ({ token, rewardId }: DeleteRewardParams): Promise<void> => {
    return fetchAPI<void>("/delete-reward", "POST", { rewardId }, token);
};

export const updateRewardStatus = async ({
    token,
    rewardId,
    status,
}: UpdateRewardStatusParams): Promise<{ reward: RewardResponse }> => {
    return fetchAPI<{ reward: RewardResponse }>("/update-reward-status", "POST", { rewardId, status }, token);
};

// Helper function to get tags via trackId (for use with progression configs)
export interface GetTagsParams {
    token: string;
    trackId: string;
}

export const getTags = async ({ token, trackId }: GetTagsParams): Promise<{ tags: ScannableObjectTagResponse[] }> => {
    // First get scanConfig to get scanConfigId
    const scanConfigResponse = await getScanConfig({ token, trackId });
    if (!scanConfigResponse?.scanConfig) {
        return { tags: [] };
    }
    // Then get tags using scanConfigId
    const tagsResponse = await getScannableObjectTags({
        token,
        scanConfigId: scanConfigResponse.scanConfig.id,
    });
    // Map scannableObjectTags to tags
    return { tags: tagsResponse.scannableObjectTags };
};
